/**
 * useStockDetail — fetches comprehensive stock data for the detail page.
 * Simple useState/useEffect hook (no Zustand needed — per-ticker, not global state).
 */
import { useState, useEffect, useRef } from 'react'
import { getStockDetail } from '@/lib/api'
import type { StockDetail } from '@/lib/types'

interface StockDetailState {
  data: StockDetail | null
  loading: boolean
  error: string | null
  refetch: () => void
}

export function useStockDetail(ticker: string): StockDetailState {
  const [data, setData] = useState<StockDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const fetch = () => {
    if (!ticker) return
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl

    setLoading(true)
    setError(null)

    getStockDetail(ticker)
      .then(d => {
        if (!ctrl.signal.aborted) {
          setData(d)
          setLoading(false)
        }
      })
      .catch(err => {
        if (!ctrl.signal.aborted) {
          setError(err?.response?.data?.detail ?? err?.message ?? 'Failed to load stock data')
          setLoading(false)
        }
      })
  }

  useEffect(() => {
    fetch()
    return () => abortRef.current?.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker])

  return { data, loading, error, refetch: fetch }
}
